<template>
  <div
    class="search-blog"
    :class="{ active: isActive }"
  >
    <UiComponentIcon
      type="x"
      @click="emit('close:search')"
    />
    <UiComponentContainer>
      <div class="search">
        <UiComponentInput
          :placeholder="placeholder"
          @input:change="searchFilter = $event"
        />
        <UiComponentIcon
          type="search"
          @click="onSubmit"
        />
      </div>
      <div class="blog-posts">
        <MoleculeBlogPost
          v-for="post in posts"
          :key="post.id"
          as="paragraph"
          :minutes-read="props.minutesRead"
          :post="post"
          type="horizontal"
        />
      </div>
    </UiComponentContainer>
  </div>
</template>

<script setup>
// Composables
const { track } = useAnalyticsJS()
const { locale } = useI18n()
const slots = useSlots()

// Emitter
const emit = defineEmits([
  'close:search',
  'on:submit',
])

// Props
const props = defineProps({
  isActive: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  minutesRead: {
    default() {
      return 'min. read'
    },
    required: true,
    type: String,
  },
  posts: {
    default: () => [],
    required: false,
    type: Array,
  },
})

// Refs
const searchFilter = ref('')

// Computed Properties
const placeholder = computed(() => {
  return findContent(slots.default() || [{ children: '' }])
})

// Watchers
watch(() => props.isActive, (value) => {
  if (value) {
    document.body.classList.add('no-scroll')
  }
  else {
    document.body.classList.remove('no-scroll')
  }
})

// Methods
function findContent(nodes) {
  const [{ children }] = nodes
  if (Array.isArray(children)) {
    return findContent(children)
  }
  else {
    return children
  }
}

function onKeypress(e) {
  if (props.isActive && e.code === 'Enter') {
    onSubmit()
  }
}

function onSubmit() {
  if (searchFilter.value.trim().length === 0) {
    return
  }

  track('Term searched', {
    category: 'Search',
    label: 'Blog search',
    searched_term: searchFilter.value,
    locale: locale.value,
  })

  emit('on:submit', searchFilter.value)
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  document.body.classList.remove('no-scroll')
  window.removeEventListener('keypress', onKeypress)
})

onMounted(() => {
  window.addEventListener('keypress', onKeypress)
})
</script>

<style lang="postcss" scoped>
.search-blog {
  @apply fixed inset-0 pb-8 flex flex-col items-center z-50 opacity-0 invisible transition-all duration-300 ease-out overflow-scroll;

  &.active {
    @apply visible opacity-100;
    backdrop-filter: blur(12px);
    background-color: rgba(255, 255, 255, 0.96);
  }

  .icon {
    @apply absolute text-3xl text-grey-blue-600 cursor-pointer;
    right: 16px;
    top: 16px;

    @screen md {
      @apply text-5xl;
      right: 32px;
      top: 32px;
    }
  }

  .container {
    @apply flex-col;

    @screen md {
      @apply px-0;
      width: 530px;
    }

    @screen xl {
      width: 620px;
    }

    .search {
      @apply relative mt-20;

      @screen md {
        @apply mt-26;
      }

      @screen xl {
        @apply mt-32;
        width: 620px;
      }

      input {
        @apply px-6 h-16;

        &::placeholder {
          @apply text-grey-blue-600 text-lg leading-16;
        }
      }

      .icon-search {
        @apply absolute text-2xl text-grey-blue-900 cursor-pointer;
        top: 20px;
        right: 20px;
        bottom: 20px;
      }
    }

    .blog-posts {
      @apply mt-16;

      @screen xl {
        @apply mt-32;
      }

      .blog-post {
        @apply mt-8;

        &:first-child {
          @apply mt-0;
        }
      }
    }
  }
}
</style>
